import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import { api } from './client';
import type {
  BudgetPeriodInput,
  BudgetPeriodOverview,
  BudgetPeriod,
  Group,
  GroupInput,
  Investment,
  InvestmentDetail,
  InvestmentInput,
  PeriodPlan,
  Position,
  PositionInput,
  Settings,
  Summary,
} from './types';

export const keys = {
  groups: ['groups'] as const,
  investments: (filter?: string) => ['investments', filter ?? 'all'] as const,
  investment: (id: string) => ['investment', id] as const,
  summary: ['summary'] as const,
  settings: ['settings'] as const,
  periods: ['budget-periods'] as const,
  periodPlan: (id: string) => ['budget-period-plan', id] as const,
};

/** Every mutation touches the totals, so they all refresh the same set. */
function useInvalidateAll() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: ['groups'] });
    void client.invalidateQueries({ queryKey: ['investments'] });
    void client.invalidateQueries({ queryKey: ['investment'] });
    void client.invalidateQueries({ queryKey: ['summary'] });
    void client.invalidateQueries({ queryKey: ['settings'] });
    void client.invalidateQueries({ queryKey: ['budget-periods'] });
    void client.invalidateQueries({ queryKey: ['budget-period-plan'] });
  };
}

export const useGroups = () =>
  useQuery({ queryKey: keys.groups, queryFn: () => api.get<Group[]>('/groups') });

export const useInvestments = (query = '') =>
  useQuery({
    queryKey: keys.investments(query),
    queryFn: () => api.get<Investment[]>(`/investments${query}`),
  });

export const useInvestment = (id: string | null) =>
  useQuery({
    queryKey: keys.investment(id ?? ''),
    queryFn: () => api.get<InvestmentDetail>(`/investments/${id}`),
    enabled: id !== null,
  });

export const useSummary = () =>
  useQuery({ queryKey: keys.summary, queryFn: () => api.get<Summary>('/summary') });

export const useSettings = () =>
  useQuery({ queryKey: keys.settings, queryFn: () => api.get<Settings>('/settings') });

/** Small helper so each mutation below is a one-liner. */
function useApiMutation<TVars, TData>(
  fn: (vars: TVars) => Promise<TData>,
): UseMutationResult<TData, Error, TVars> {
  const invalidate = useInvalidateAll();
  return useMutation({ mutationFn: fn, onSuccess: invalidate });
}

export const useCreateGroup = () =>
  useApiMutation((input: GroupInput) => api.post<Group>('/groups', input));

export const useUpdateGroup = () =>
  useApiMutation(({ id, ...input }: GroupInput & { id: string }) =>
    api.patch<Group>(`/groups/${id}`, input),
  );

export const useDeleteGroup = () => useApiMutation((id: string) => api.delete(`/groups/${id}`));

export const useReorderGroups = () =>
  useApiMutation((ids: string[]) => api.post<Group[]>('/groups/reorder', { ids }));

export const useCreateInvestment = () =>
  useApiMutation((input: InvestmentInput) => api.post<Investment>('/investments', input));

export const useUpdateInvestment = () =>
  useApiMutation(({ id, ...input }: Partial<InvestmentInput> & { id: string }) =>
    api.patch<Investment>(`/investments/${id}`, input),
  );

export const useDeleteInvestment = () =>
  useApiMutation((id: string) => api.delete(`/investments/${id}`));

export const useReorderInvestments = () =>
  useApiMutation((ids: string[]) => api.post<Investment[]>('/investments/reorder', { ids }));

export const useCreatePosition = () =>
  useApiMutation(({ investmentId, ...input }: PositionInput & { investmentId: string }) =>
    api.post<Position>(`/investments/${investmentId}/positions`, input),
  );

export const useUpdatePosition = () =>
  useApiMutation(({ id, ...input }: Partial<PositionInput> & { id: string }) =>
    api.patch<Position>(`/positions/${id}`, input),
  );

export const useDeletePosition = () =>
  useApiMutation((id: string) => api.delete(`/positions/${id}`));

export const useReorderPositions = () =>
  useApiMutation(({ investmentId, ids }: { investmentId: string; ids: string[] }) =>
    api.post<Position[]>(`/investments/${investmentId}/positions/reorder`, { ids }),
  );

export const useUpdateSettings = () =>
  useApiMutation((input: Partial<Settings>) => api.patch<Settings>('/settings', input));

export const useBudgetPeriods = () =>
  useQuery({
    queryKey: keys.periods,
    queryFn: () => api.get<BudgetPeriodOverview[]>('/budget-periods'),
  });

export const usePeriodPlan = (id: string | null) =>
  useQuery({
    queryKey: keys.periodPlan(id ?? ''),
    queryFn: () => api.get<PeriodPlan>(`/budget-periods/${id}/plan`),
    enabled: id !== null,
  });

export const useCreatePeriod = () =>
  useApiMutation((input: BudgetPeriodInput) => api.post<BudgetPeriod>('/budget-periods', input));

export const useUpdatePeriod = () =>
  useApiMutation(({ id, ...input }: Partial<BudgetPeriodInput> & { id: string }) =>
    api.patch<BudgetPeriod>(`/budget-periods/${id}`, input),
  );

export const useDeletePeriod = () =>
  useApiMutation((id: string) => api.delete(`/budget-periods/${id}`));
